<template>
    <div class="checkbox-wrapper">
        <div
        class="input-elements-wrapper"
        :class="{ 'disabled': disabled }"
        @click.stop="clickedHandler">
            <div class="input-container">
                <!-- Not-selected icon -->
                <component
                v-if="!selected"
                class="icon-unselected"
                :is="iconUnselectedComponent"
                :class="{ 'disabled': disabled, 'error-state': errorState }"/>
                <!-- Selected Icon -->
                <checkbox-selected
                v-else
                class="icon-selected"
                :class="{ 'disabled': disabled }"/>
                <!-- Input Checkbox -->
                <input
                type="checkbox"
                :class="{ 'disabled': disabled, 'selected': selected }"
                :id="`checkbox-${id}`"
                :value="value"
                :disabled="disabled"
                :checked="selected">
            </div>
            <!-- *********************************************
            *                      Label                     *
            ********************************************** -->
            <label
            v-if="label"
            v-html="parsedLabel"
            :class="{ 'disabled': disabled, 'selected': selected }"/>
        </div>
        <!-- *********************************************
        *                  Error Message                 *
        ********************************************** -->
        <div v-if="errorMessage && errorState && !selected" class='error-message'>{{ errorMessage }}</div>
        <slot></slot>
    </div>
</template>
margin-top: 6px;

<script setup>
import CheckboxSelected from '@/assets/icons/checkbox-selected.svg?inline'
/* Vue */
import { computed, defineAsyncComponent } from 'vue'

/* Emmitters */
const emit = defineEmits(['clicked'])

/** Props received
@id { String, Number } - checkbox id
@label { String } - label
@disabled { Boolean } - component is disabled or not
@selected { Boolean } - checkbox is selected or not
@errorState { Boolean } - property to show if checkbox has error or not
@errorMessage { String }  - error message to display
@linkIsLocalFile {Boolean} prop to tells us if we need to concat with link our env base url
*/
const props = defineProps({
    id: String || Number,
    label: String,
    disabled: Boolean,
    selected: Boolean,
    errorState: Boolean,
    errorMessage: String,
    linkIsLocalFile: Boolean
})

const parsedLabel = computed(() => {
    if (window.cordova && window.cordova.platformId === 'ios' && props.label.includes('</a>')) {
        return props.label.replace('<a', '<span').replace('</a>', '</span>')
	}
    return props.label
})

/* Return which icon to use on the checkbox when it unselected (errorState or not) */
const iconUnselectedComponent = computed(() => {
    const component = props.errorState ? 'checkbox-error' : 'checkbox'
    return defineAsyncComponent(() => import('@/assets/icons/' + component + '.svg?inline'))
})

const clickedHandler = event => {
    if (event.target.tagName === 'A' || props.disabled) return
    /* This is an attempt to this generic through the app, we might need to change this if errors come in future in specific cases */
    if (window.cordova && window.cordova.platformId === 'ios' && event.target.tagName === 'SPAN' && props.label.includes('</a>')) {
        let link = props.label.split('href=')[1].split(' target=')[0].replace(' target', '')
        if (props.linkIsLocalFile) link = process.env.VUE_APP_WEB_BASE_URL + link.replace('./', '/')
        window.cordova.InAppBrowser.open(link, '_system', 'location=yes,hidden=yes')
        return
    }
    emit('clicked', props.id)
}
</script>

<style lang="scss" scoped>
.checkbox-wrapper {
    display: flex;
    align-items: flex-start;
    flex-direction: column;

    .input-elements-wrapper {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        cursor: pointer;

        &.disabled {
            cursor: default;
            pointer-events: none;
        }

        .input-container {
            display: flex;
            justify-content: center;
            align-items: center;

            position: relative;

            .icon-unselected {
                width: 20px;
                height: 20px;

                position: absolute;

                &.disabled {
                    opacity: 0.5;
                }

                &.error-state {
                    :deep(.change-color) {
                        stroke: var(--negative);
                    }
                }
            }

            .icon-selected {
                width: 20px;
                height: 20px;

                position: absolute;

                :deep(.change-color-fill) {
                    fill: var(--primary);
                }

                :deep(.change-color-fill-inside) {
                    fill: var(--secondary-light-4);
                }

                &.disabled {
                    opacity: 0.5;
                }
            }
        }

        label {
            @include H7-Regular;
            line-height: 20px;
            color: var(--secondary-dark-3);

            cursor: pointer;
            -webkit-user-select: none; /* Safari */
            -ms-user-select: none; /* IE 10 and IE 11 */
            user-select: none; /* Standard syntax */

            &.disabled {
                color: var(--secondary-dark-3);
                opacity: 0.5;
                cursor: default;
            }

            &.selected {
                @include H7-Medium;
                color: var(--primary);

                &.disabled {
                    opacity: 0.5;
                    cursor: default;
                }
            }

            :deep(a), :deep(span) {
                @include H7-Medium;
                text-decoration: underline;
                text-underline-offset: 3px;
                color: var(--primary);
            }
        }

        input {
            width: 20px;
            height: 20px;
            flex-shrink: 0;

            position: relative;
            appearance: none;

            cursor: pointer;

            &.disabled {
                pointer-events: none;
                cursor: default;
            }
        }
    }

    .error-message {
        @include H7-Regular;
        line-height: 16px;
        color: var(--negative);
        margin-top: 6px;
    }

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            .input-elements-wrapper {
                .input-container {
                    .icon-unselected {
                        &:not(.disabled) {
                            :deep(.change-color-fill) {
                                fill: var(--secondary);
                            }
                        }

                        &.error-state {
                            :deep(.change-color-fill) {
                                fill: var(--button-secondary-state-hover);
                            }
                        }
                    }
                }
            }
        }
    }

    &:focus {
        .input-elements-wrapper {
            .input-container {
                .icon-unselected {
                    &:not(.disabled) {
                        :deep(.change-color-fill) {
                            fill: var(--secondary-dark-1);
                        }
                    }

                    &.error-state {
                        :deep(.change-color-fill) {
                            fill: var(--button-secondary-state-press);
                        }
                    }
                }
            }
        }
    }
}
</style>
